<template>
  <div
    class="speaker-info-modal"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('Components.SpeakerInfoModal.speakerDetails')"
  >
    <button
      class="speaker-info-modal__close"
      @click="onClose"
      :aria-label="$t('Components.SpeakerInfoModal.close')"
    >
      <i class="fas fa-times"></i>
    </button>

    <div class="speaker-info-modal__header">
      <div class="speaker-info-modal__avatar-wrap">
        <img
          v-if="speaker && speaker.profileImageUrl"
          :src="speaker.profileImageUrl"
          :alt="fullName || $t('Components.SpeakerInfoModal.speakerPhoto')"
          class="speaker-info-modal__avatar"
        />
        <div v-else class="speaker-info-modal__avatar-placeholder">
          <i class="far fa-user"></i>
        </div>
      </div>
      <h2 class="speaker-info-modal__name">{{ fullName }}</h2>
      <div v-if="speaker && speaker.jobTitle" class="speaker-info-modal__title">
        {{ speaker.jobTitle }}
      </div>
      <div
        v-if="speaker && speaker.company"
        class="speaker-info-modal__company"
      >
        {{ speaker.company }}
      </div>
    </div>

    <div
      v-if="speaker && speaker.description"
      class="speaker-info-modal__description html-content"
      v-html="speaker.description"
    />

    <div v-if="hasContactDetails" class="speaker-info-modal__details">
      <div v-if="speaker.email" class="detail-row">
        <div class="detail-row__icon detail-row__icon--teal">
          <i class="far fa-envelope"></i>
        </div>
        <a :href="'mailto:' + speaker.email" class="detail-row__link">
          {{ speaker.email }}
        </a>
      </div>

      <div v-if="speaker.phoneNumber" class="detail-row">
        <div class="detail-row__icon detail-row__icon--green">
          <i class="fas fa-phone"></i>
        </div>
        <a :href="'tel:' + speaker.phoneNumber" class="detail-row__link">
          {{ speaker.phoneNumber }}
        </a>
      </div>

      <div
        v-for="(link, idx) in normalizedLinks"
        :key="`speaker-link-${idx}`"
        class="detail-row"
      >
        <div class="detail-row__icon detail-row__icon--purple">
          <i :class="iconForLink(link)"></i>
        </div>
        <a
          :href="normalizeUrl(link.url || link.href || link)"
          target="_blank"
          rel="noopener"
          class="detail-row__link"
        >
          {{
            link.label ||
            link.name ||
            link.type ||
            link.url ||
            link.href ||
            link
          }}
        </a>
      </div>
    </div>

    <div v-if="sessions.length" class="speaker-info-modal__sessions">
      <h3 class="speaker-info-modal__sessions-title">
        <i class="far fa-calendar-alt" aria-hidden="true"></i>
        {{ $t('Components.SpeakerInfoModal.sessions') }}
      </h3>
      <div class="session-list">
        <div v-for="session in sessions" :key="session.id" class="session-card">
          <div class="session-card__time">
            <div class="session-card__date">
              {{ formatSessionDate(session.startDate) }}
            </div>
            <div class="session-card__hours">
              {{ formatSessionTime(session.startDate) }}
              <span v-if="session.endDate">
                – {{ formatSessionTime(session.endDate) }}</span
              >
            </div>
          </div>
          <div class="session-card__body">
            <div class="session-card__title">{{ session.title }}</div>
            <div
              v-if="session.locationName || session.location"
              class="session-card__location"
            >
              <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
              {{ session.locationName || session.location }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="speaker-info-modal__footer">
      <button class="btn-close-secondary" @click="onClose">
        {{ $t('Components.SpeakerInfoModal.close') }}
      </button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import focusTrapMixin from '@/mixins/focusTrap';

export default {
  name: 'SpeakerInfoModal',
  mixins: [focusTrapMixin],
  props: {
    speaker: { type: Object, default: null }
  },
  computed: {
    fullName() {
      if (!this.speaker) return '';
      return `${this.speaker.name || ''}${
        this.speaker.lastName ? ' ' + this.speaker.lastName : ''
      }`.trim();
    },
    sessions() {
      return this.speaker && Array.isArray(this.speaker.sessions)
        ? this.speaker.sessions
        : [];
    },
    normalizedLinks() {
      const raw = this.speaker && this.speaker.links;
      if (!raw) return [];
      if (Array.isArray(raw)) return raw.filter(Boolean);
      if (typeof raw === 'object') return Object.values(raw).filter(Boolean);
      return [];
    },
    hasContactDetails() {
      return !!(
        this.speaker &&
        (this.speaker.email ||
          this.speaker.phoneNumber ||
          this.normalizedLinks.length)
      );
    }
  },
  mounted() {
    this.activateFocusTrap();
  },
  beforeDestroy() {
    this.deactivateFocusTrap();
  },
  methods: {
    onClose() {
      document.body.classList.remove('overflow-hidden');
      this.$emit('close');
    },
    normalizeUrl(url) {
      if (!url) return url;
      const trimmed = String(url).trim();
      if (/^(mailto:|tel:|https?:\/\/)/i.test(trimmed)) return trimmed;
      return `https://${trimmed}`;
    },
    iconForLink(link) {
      const value = String(
        link.type || link.label || link.name || link.url || link.href || link
      ).toLowerCase();
      if (value.includes('linkedin')) return 'fab fa-linkedin-in';
      if (value.includes('twitter') || value.includes('x.com'))
        return 'fab fa-twitter';
      if (value.includes('facebook')) return 'fab fa-facebook-f';
      if (value.includes('instagram')) return 'fab fa-instagram';
      if (value.includes('youtube')) return 'fab fa-youtube';
      if (value.includes('github')) return 'fab fa-github';
      return 'fas fa-globe';
    },
    formatSessionDate(date) {
      if (!date) return '';
      return Vue.moment(date).format('ddd, MMM D');
    },
    formatSessionTime(date) {
      if (!date) return '';
      return Vue.moment(date).format('HH:mm');
    }
  }
};
</script>

<style scoped lang="scss">
.speaker-info-modal {
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  padding: 32px 28px 24px;
  font-family: 'Poppins', sans-serif;
  max-height: 90vh;
  overflow-y: auto;

  &__close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: #f3f4f6;
    color: #374151;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    transition: background 0.15s;

    &:hover {
      background: #e5e7eb;
    }
  }

  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 20px;
    padding-right: 32px;
  }

  &__avatar-wrap {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
    overflow: hidden;
  }

  &__avatar {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__avatar-placeholder {
    font-size: 42px;
    color: #d1d5db;
  }

  &__name {
    font-size: 22px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 4px;
    line-height: 1.25;
  }

  &__title {
    font-size: 14px;
    color: #374151;
    margin-top: 2px;
  }

  &__company {
    font-size: 13px;
    color: #6b7280;
    margin-top: 2px;
  }

  &__description {
    font-size: 14px;
    color: #374151;
    line-height: 1.65;
    border-top: 1px solid #f3f4f6;
    padding-top: 18px;
    margin-bottom: 20px;
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;
  }

  &__sessions {
    border-top: 1px solid #f3f4f6;
    padding-top: 18px;
    margin-bottom: 20px;
  }

  &__sessions-title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 12px;
    display: flex;
    align-items: center;
    gap: 8px;

    i {
      color: var(--color-primary, #047873);
    }
  }

  &__footer {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
    border-top: 1px solid #f3f4f6;
    padding-top: 18px;
  }
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 14px;

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;

    &--teal {
      background: #e6f7f5;
      color: var(--color-primary, #047873);
    }
    &--purple {
      background: #f3f0ff;
      color: #7c3aed;
    }
    &--green {
      background: #ecfdf5;
      color: #16a34a;
    }
  }

  &__link {
    font-size: 14px;
    color: var(--color-primary, #047873);
    text-decoration: none;
    word-break: break-word;

    &:hover {
      text-decoration: underline;
    }
  }
}

.session-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.session-card {
  display: flex;
  gap: 14px;
  padding: 12px 14px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;

  &__time {
    flex: 0 0 92px;
    border-right: 1px solid #e5e7eb;
    padding-right: 12px;
  }

  &__date {
    font-size: 11px;
    font-weight: 700;
    color: var(--color-primary, #047873);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__hours {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    margin-top: 2px;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    line-height: 1.4;
  }

  &__location {
    font-size: 12px;
    color: #6b7280;
    margin-top: 4px;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    i {
      color: #6b7280;
    }
  }
}

.btn-close-secondary {
  padding: 10px 20px;
  background: #ffffff;
  color: #374151;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: var(--color-primary, #047873);
    color: var(--color-primary, #047873);
  }
}

::v-deep .html-content {
  p {
    margin-bottom: 10px;
  }
  h1,
  h2,
  h3 {
    font-weight: 700;
    margin-bottom: 8px;
  }
  ul,
  ol {
    padding-left: 20px;
    margin-bottom: 10px;
  }
  a {
    color: var(--color-primary, #047873);
  }
}
</style>
